import moment from 'moment';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { GroupBy, SchedulerReference } from '../types';
import {
  arrangeProjectPhases,
  phaseAsEvent,
} from '../utils/events-conversion/project-phase';
import { resourceIdForProject } from '../utils/id';

interface Props {
  schedulerRef: SchedulerReference;
  groupBy: GroupBy;
}
interface ReturnValue {
  updateProjectTimeline: (project: ProjectId) => void;
  loadProjectPhases: (project: Project) => void;
}
export const useProjectTimelineManager = (props: Props): ReturnValue => {
  const queryClient = useQueryClient();
  const { schedulerRef, groupBy } = props;

  const loadProjectPhases = useCallback(
    (project: Project, force = false) => {
      const resource =
        schedulerRef.current?.instance.project.resourceStore.getById(
          resourceIdForProject(project.projectId),
        );

      if (resource == null || (!force && resource.getEvents().length > 1)) {
        return;
      }

      schedulerRef.current.instance.suspendRefresh();
      const projectPhaseEvents = arrangeProjectPhases(
        project.projectPhases,
      ).map((phase) => phaseAsEvent(project.projectId, phase));
      const addedEvents =
        schedulerRef.current.instance.eventStore.add(projectPhaseEvents);

      if (addedEvents != null && resource.isExpanded()) {
        addedEvents.forEach((e) => e.set('showInDetail', true));
      }
      schedulerRef.current.instance.resumeRefresh(true);
    },
    [schedulerRef],
  );

  const updateProjectTimeline = useCallback(
    ([, projectId]: ProjectId) => {
      if (groupBy === GroupBy.Users) {
        return;
      }
      const latestProjects =
        queryClient.getQueryData<Project[]>(ALL_PROJECTS_QUERY_KEY) ?? [];

      const project = latestProjects.find((x) => x.projectId === projectId);

      if (project == null || schedulerRef.current == null) {
        return;
      }
      schedulerRef.current.instance.suspendRefresh();
      const projectTimelineEvent =
        schedulerRef.current.instance.eventStore.getById(
          `${project.projectId}_timeline`,
        );

      if (projectTimelineEvent) {
        projectTimelineEvent.setStartDate(
          moment(project.startDate).startOf('day').toDate(),
          false,
        );
        projectTimelineEvent.setEndDate(
          moment(project.dueDate).endOf('day').toDate(),
          false,
        );
      }
      loadProjectPhases(project, true);
      schedulerRef.current.instance.resumeRefresh(true);
    },
    [groupBy, loadProjectPhases, queryClient, schedulerRef],
  );

  return {
    updateProjectTimeline,
    loadProjectPhases,
  };
};
